import React, { useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Margin } from 'styled-components-spacing';
import {
  Alerts,
  Alert,
  Col,
  Container,
  Field,
  Header,
  Row,
  PageContent,
  Button,
  Spinner,
  Heading,
} from 'components';
import {
  sendMaReportCreator,
  setMaReportErrorsCreator,
  loadMaLastReportCreator,
} from 'store/actionsCreators';
import { checkPermissions, isLoading, globalPropTypes } from 'helpers';
import clearErrors from 'helpers/clearErrors';
import editReducer, {
  defaultState,
  yearFieldsKeys,
  updateFieldCreator,
  readOnlyFields,
  setEditDataCreator,
} from './reducer';
import HowToFill from '../HowToFill/HowToFill';
import YoungCrewSection from '../YoungCrewSection/YoungCrewSection';
import { ReportContainer } from '../Reports.styled';
import { loadMaReportInfoCreator } from '../../../../store/actionsCreators';

const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.ma.reports',
    url: '/ma/reports',
  },
  {
    title: 'navigation.breadcrumbs.ma.addReport',
    url: '',
  },
];

const AddReport = ({
  loading,
  sendReport,
  userPermissions,
  errors,
  removeError,
  history,
  loadReportInfo,
  reportInfo,
  youngCrew,
}) => {
  const { t } = useTranslation();
  const [report, dispatch] = useReducer(editReducer, defaultState);

  useEffect(() => {
    loadReportInfo();
  }, [loadReportInfo]);

  useEffect(() => {
    if (reportInfo) {
      dispatch(setEditDataCreator(reportInfo.report));
    }
  }, [reportInfo]);

  const submit = () => {
    const params = new FormData();
    Object.keys(report).forEach((category) => {
      Object.keys(report[category]).forEach((field) => {
        if (!field.toLowerCase().includes('notes')) {
          params.append(
            `${yearFieldsKeys[0]}[${field}]`,
            report[category][field][yearFieldsKeys[0]],
          );
        } else {
          params.append(`${field}`, report[category][field].notes);
        }
      });
    });

    sendReport(params, history);
  };

  const handleInputChange = (group, field, inner, errorName, isNumber = false) => (e) => {
    if (isNumber && isNaN(+e.target.value)) return;

    dispatch(
      updateFieldCreator({
        name: group,
        value: {
          ...report[group],
          [field]: { ...report[group][field], [inner]: e.target.value },
        },
      }),
    );
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const renderInput = (category, field, forYear) => (
    <Margin bottom="20" as={Col}>
      <Field
        label={
          category == 'roles'
            ? t('page.ma.addReport.nameSurname')
            : t(`page.ma.addReport.${forYear}`, { year: new Date().getFullYear() })
        }
        error={errors[`${forYear}[${field}]`]}
      >
        <input
          id={`${category}${field}${forYear}`}
          type="text"
          placeholder={readOnlyFields.includes(field) ? '' : t('page.ma.addReport.placeholder')}
          value={report[category][field][forYear]}
          disabled={readOnlyFields.includes(field)}
          onChange={handleInputChange(category, field, forYear, undefined, true)}
        />
      </Field>
    </Margin>
  );

  const renderTextArea = (category, field) => (
    <Margin bottom="20" as={Col} size={{ lg: 3 / 4 }}>
      <Field label={t('page.ma.addReport.additionalNotes')} error={errors[field]}>
        <textarea
          id={`${category}${field}`}
          placeholder={
            readOnlyFields.includes(field) ? '' : t('page.ma.addReport.placeholderTextArea')
          }
          value={report[category][field].notes}
          disabled={readOnlyFields.includes(field)}
          onChange={handleInputChange(category, field, 'notes')}
        />
      </Field>
    </Margin>
  );

  const renderRow = (category) => {
    const categoryStyle = { minHeight: '45px' };

    return (
      <>
        <ReportContainer>
          {Object.keys(report[category])
            .filter((field) => !Object.keys(report[category][field]).includes('notes'))
            .map((field, index) => (
              <Margin bottom="16" key={`${category}-${field}`}>
                <Margin
                  style={category === 'membership' && index < 6 ? categoryStyle : {}}
                  bottom="8"
                >
                  <Heading size="h5">{t(`page.ma.addReport.${field}`)}</Heading>
                </Margin>
                <Row>
                  {Object.keys(report[category][field]).map((forYear) =>
                    renderInput(category, field, forYear),
                  )}
                </Row>
                <Margin bottom="2">
                  {t(`page.ma.addReport.notes.${field}`) !== `page.ma.addReport.notes.${field}` &&
                    t(`page.ma.addReport.notes.${field}`)
                      .split('<br />')
                      .map((line) => <div>{line}</div>)}
                </Margin>
              </Margin>
            ))}
        </ReportContainer>

        {Object.keys(report[category])
          .filter((field) => Object.keys(report[category][field]).includes('notes'))
          .map((field) => (
            <Row>{renderTextArea(category, field)}</Row>
          ))}
      </>
    );
  };

  return (
    <>
      <Header breadcrumbs={breadcrumbs} title={t('page.ma.addReport.title')} />
      <Container>
        <PageContent>
          {checkPermissions(
            {
              roles: ['ma_send_members_report'],
            },
            userPermissions,
          ) ? (
            <>
              {loading ? <Spinner /> : null}
              {!loading ? (
                <Col>
                  <Alerts type="ma" />
                  <HowToFill />
                  {Object.keys(report).map((category) => (
                    <Margin bottom="36">
                      <Margin bottom="24">
                        <Heading size="h4">{t(`page.ma.addReport.${category}`)}</Heading>
                        {t(`page.ma.addReport.notes.${category}`) !==
                          `page.ma.addReport.notes.${category}` &&
                          t(`page.ma.addReport.notes.${category}`)
                            .split('<br />')
                            .map((line) => <div style={{ marginTop: '10px' }}>{line}</div>)}
                      </Margin>
                      {renderRow(category)}
                    </Margin>
                  ))}

                  <YoungCrewSection youngCrew={youngCrew} />

                  <Row>
                    <Col size={{ lg: 1 / 4 }}>
                      <Margin top={{ xs: 12 }}>
                        <Button type="button" onClick={submit}>
                          {t('page.ma.addReport.send')}
                        </Button>
                      </Margin>
                    </Col>
                  </Row>
                </Col>
              ) : null}
            </>
          ) : (
            <Alert type="info">{t('page.forbidden.subtitle')}</Alert>
          )}
        </PageContent>
      </Container>
    </>
  );
};

AddReport.defaultProps = {
  errors: {},
  youngCrew: null,
};

AddReport.propTypes = {
  loading: PropTypes.bool.isRequired,
  // lastReport: PropTypes.shape({}).isRequired,
  sendReport: PropTypes.func.isRequired,
  // loadLastReport: PropTypes.func.isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  errors: PropTypes.shape({
    'actualYearReport[individualMembers]': PropTypes.string,
    'actualYearReport[individualMembersF]': PropTypes.string,
    'actualYearReport[individualMembersM]': PropTypes.string,
    'actualYearReport[nationalYoungCrewMembers]': PropTypes.string,
    'actualYearReport[students]': PropTypes.string,
    'actualYearReport[corporateMembers]': PropTypes.string,
    'actualYearReport[activeVolunteers]': PropTypes.string,
    'actualYearReport[eventsNumber]': PropTypes.string,
    'actualYearReport[eventsTotalParticipants]': PropTypes.string,
    'actualYearReport[trainingsProvided]': PropTypes.string,
    'actualYearReport[regPartners]': PropTypes.string,
    membershipNotes: PropTypes.string,
    eventsNotes: PropTypes.string,
    educationNotes: PropTypes.string,
  }),
  removeError: PropTypes.func.isRequired,
  youngCrew: PropTypes.shape({}),
  history: PropTypes.shape({}).isRequired,
};

const mapStateToProps = ({ ma, user, loading }) => ({
  lastReport: ma.report,
  loading: isLoading(loading, 'MA_REPORT'),
  userPermissions: user.userPermissions,
  reportInfo: ma.reportInfo,
  youngCrew: ma.reportInfo ? ma.reportInfo.youngCrew : null,
  errors: ma.reportErrors,
});

const mapDispatchToProps = (dispatch) => ({
  sendReport: (payload, history) => dispatch(sendMaReportCreator(payload, history)),
  removeError: (payload) => dispatch(setMaReportErrorsCreator(payload)),
  loadLastReport: () => dispatch(loadMaLastReportCreator({ editLatestReport: false })),
  loadReportInfo: () => dispatch(loadMaReportInfoCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddReport);
